import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { getRoleHome, useAuth } from "../auth/AuthContext";
import { ARAGON_COLORS, useAragonViewport } from "../theme/AragonTheme";

const LOGO_SRC = "/images/1080 x 1080 px Logo 1.png";
const AUTH_VISUAL_SRC = "/images/Login Image.jpg";
const AUTH_SURFACE = "#F8F7F4";
const AUTH_FIELD = "#F2F2F0";
const AUTH_BORDER = "#D9D9D4";
const LEGAL_VERSION = "2026-08-27";
const AUTH_MODES = new Set([
  "login",
  "register",
  "verify",
  "forgot-password",
  "reset-password",
  "mfa",
  "callback",
]);

const MODE_TITLE = {
  login: "Welcome to Aragon",
  register: "Welcome to Aragon",
  verify: "Verify your email",
  "forgot-password": "Forgot your password?",
  "reset-password": "Reset your password",
  mfa: "Security verification",
  callback: "Completing sign in",
};

const MODE_SUBTITLE = {
  login: "Access your account and continue your journey with Aragon.",
  register: "Create your account and begin your transaction journey with Aragon.",
  verify: "Confirm your email before continuing into Aragon.",
  "forgot-password": "Enter your account email to continue with secure verification.",
  "reset-password": "Choose a new password for your Aragon account.",
  mfa: "Complete the additional security check for this session.",
  callback: "Aragon is validating your secure session.",
};

const MODE_HELP = {
  verify: "Enter the six-digit code sent to your email address.",
  "forgot-password": "If the email exists, Aragon will send a six-digit verification code.",
  "reset-password": "Choose and confirm the new password for your account.",
  mfa: "Enter the six-digit code from your authenticator to continue.",
};

function MessageBox({ tone = "info", children }) {
  const source = tone === "error" || tone === "warning" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel;
  return (
    <Box
      role={tone === "error" || tone === "warning" ? "alert" : "status"}
      sx={{
        border: `1px solid ${alpha(source, tone === "warning" ? 0.32 : 0.46)}`,
        bgcolor: alpha(source, tone === "warning" ? 0.11 : 0.08),
        color: tone === "warning" ? "#171717" : tone === "error" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.navy,
        borderRadius: "10px",
        px: 1.5,
        py: 1.2,
        fontSize: 12.5,
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

function Field({ label, action, ...props }) {
  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "center",
          columnGap: 2,
          mb: 0.75,
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: ARAGON_COLORS.navy }}>
          {label}
        </Typography>
        {action}
      </Box>
      <TextField
        fullWidth
        variant="outlined"
        {...props}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: 46,
            borderRadius: "9px",
            bgcolor: AUTH_FIELD,
            color: ARAGON_COLORS.navy,
            "& fieldset": {
              borderColor: AUTH_BORDER,
            },
            "&:hover fieldset": {
              borderColor: alpha(ARAGON_COLORS.navy, 0.32),
            },
            "&.Mui-focused fieldset": {
              borderColor: ARAGON_COLORS.navy,
            },
          },
          "& input::placeholder": {
            color: alpha(ARAGON_COLORS.steel, 0.82),
            opacity: 1,
          },
        }}
      />
    </Box>
  );
}

function PasswordField({ label, value, onChange, action, autoComplete = "current-password" }) {
  const [visible, setVisible] = useState(false);
  return (
    <Field
      label={label}
      action={action}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      placeholder="••••••••••••"
      required
      InputProps={{
        endAdornment: (
          <Button
            type="button"
            onClick={() => setVisible((current) => !current)}
            disableRipple
            sx={{
              minWidth: 0,
              p: 0.6,
              color: ARAGON_COLORS.steel,
              fontSize: 11,
              fontWeight: 700,
              "&:hover": {
                bgcolor: "transparent",
                color: ARAGON_COLORS.navy,
              },
            }}
          >
            {visible ? "Hide" : "Show"}
          </Button>
        ),
      }}
    />
  );
}

function SuitcaseIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path d="M8 5.25C8 4.007 9.007 3 10.25 3h3.5C14.993 3 16 4.007 16 5.25V7h2.25A2.75 2.75 0 0 1 21 9.75v7.5A2.75 2.75 0 0 1 18.25 20H5.75A2.75 2.75 0 0 1 3 17.25v-7.5A2.75 2.75 0 0 1 5.75 7H8V5.25Zm1.75 0V7h4.5V5.25a.5.5 0 0 0-.5-.5h-3.5a.5.5 0 0 0-.5.5ZM4.75 11v6.25c0 .552.448 1 1 1h12.5a1 1 0 0 0 1-1V11h-5.375v1.1a1.875 1.875 0 0 1-3.75 0V11H4.75Zm7.125 0v1.1a.125.125 0 1 0 .25 0V11h-.25Z" />
    </SvgIcon>
  );
}

function AcquireIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path d="M10.25 4a6.25 6.25 0 1 0 3.94 11.1l4.855 4.855a1 1 0 0 0 1.414-1.414l-4.854-4.854A6.25 6.25 0 0 0 10.25 4Zm-4.5 6.25a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0Z" />
    </SvgIcon>
  );
}

function RoleChoice({ value, onChange }) {
  const options = [
    {
      value: "seller",
      icon: SuitcaseIcon,
      title: "I'm selling a business",
      body: "Get a valuation and meet qualified buyers",
    },
    {
      value: "buyer",
      icon: AcquireIcon,
      title: "I'm looking to acquire",
      body: "Browse opportunities that match your criteria",
    },
  ];
  return (
    <Box>
      <Typography sx={{ mb: 0.9, fontSize: 12.5, fontWeight: 700, color: ARAGON_COLORS.navy }}>
        I am a
      </Typography>
      <Stack spacing={1}>
        {options.map((option) => {
          const selected = value === option.value;
          const Icon = option.icon;
          return (
            <Button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              disableRipple
              aria-pressed={selected}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                textAlign: "left",
                textTransform: "none",
                borderRadius: "12px",
                border: `1px solid ${selected ? ARAGON_COLORS.burgundy : AUTH_BORDER}`,
                bgcolor: selected ? alpha(ARAGON_COLORS.burgundy, 0.045) : AUTH_FIELD,
                color: ARAGON_COLORS.navy,
                px: 1.55,
                py: 1.25,
                transition: "border-color 180ms ease, background-color 180ms ease",
                "&:hover": {
                  bgcolor: selected ? alpha(ARAGON_COLORS.burgundy, 0.065) : alpha(ARAGON_COLORS.steel, 0.07),
                  borderColor: selected ? ARAGON_COLORS.burgundy : alpha(ARAGON_COLORS.navy, 0.28),
                },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  mr: 1.2,
                  flexShrink: 0,
                  borderRadius: "9px",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: selected ? alpha(ARAGON_COLORS.burgundy, 0.09) : alpha(ARAGON_COLORS.steel, 0.08),
                  color: selected ? ARAGON_COLORS.burgundy : ARAGON_COLORS.steel,
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 13.4,
                    fontWeight: 750,
                    color: selected ? ARAGON_COLORS.burgundy : ARAGON_COLORS.navy,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography sx={{ mt: 0.2, fontSize: 11.3, lineHeight: 1.4, color: ARAGON_COLORS.steel }}>
                  {option.body}
                </Typography>
              </Box>
              <Box
                sx={{
                  ml: 1,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: `1px solid ${selected ? ARAGON_COLORS.burgundy : alpha(ARAGON_COLORS.steel, 0.42)}`,
                  display: "grid",
                  placeItems: "center",
                  color: ARAGON_COLORS.burgundy,
                  fontSize: 13,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {selected ? "✓" : ""}
              </Box>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

function OtpInput({ value, onChange, disabled = false }) {
  const refs = useRef([]);
  const digits = String(value || "").padEnd(6, " ").slice(0, 6).split("");
  const updateDigit = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, "").slice(-1);
    const current = String(value || "").padEnd(6, " ").slice(0, 6).split("");
    current[index] = digit || " ";
    onChange(current.join("").replace(/\s/g, ""));
    if (digit && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index].trim() && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };
  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) {
      return;
    }
    event.preventDefault();
    onChange(pasted);
    refs.current[Math.min(pasted.length, 6) - 1]?.focus();
  };
  return (
    <Stack direction="row" spacing={{ xs: 0.7, sm: 1 }} onPaste={handlePaste}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(node) => {
            refs.current[index] = node;
          }}
          value={digit.trim()}
          disabled={disabled}
          onChange={(event) => updateDigit(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          inputProps={{
            inputMode: "numeric",
            maxLength: 1,
            "aria-label": `Verification digit ${index + 1}`,
            style: {
              textAlign: "center",
              padding: 0,
              fontSize: 20,
              fontWeight: 700,
            },
          }}
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              height: 48,
              borderRadius: "9px",
              bgcolor: AUTH_FIELD,
              color: ARAGON_COLORS.navy,
              "& fieldset": {
                borderColor: AUTH_BORDER,
              },
              "&.Mui-focused fieldset": {
                borderColor: ARAGON_COLORS.navy,
              },
            },
          }}
        />
      ))}
    </Stack>
  );
}

function Authenticate() {
  const viewport = useAragonViewport();
  const { mode: rawMode = "login" } = useParams();
  const mode = AUTH_MODES.has(rawMode) ? rawMode : "login";
  const [searchParams] = useSearchParams();
  const requestedRegisterRole = mode === "register" && ["seller", "buyer"].includes(searchParams.get("role"))
    ? searchParams.get("role")
    : "";
  const navigate = useNavigate();
  const location = useLocation();
  const {
    status,
    user,
    login,
    register,
    verifyEmail,
    resendVerification,
    verifyPasswordReset,
    forgotPassword,
    resetPassword,
    setupMfa,
    verifyMfa,
    refreshSession,
  } = useAuth();
  const [form, setForm] = useState({
    email: location.state?.email || "",
    password: "",
    confirmPassword: "",
    role: requestedRegisterRole || "seller",
    code: "",
    accepted: false,
  });
  const [mfaSetup, setMfaSetup] = useState(null);
  const [registerStage, setRegisterStage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [recoveryWarning, setRecoveryWarning] = useState("");
  const verifyPurpose = mode === "verify" ? searchParams.get("purpose") || "" : "";
  const isPasswordResetVerification = mode === "verify" && verifyPurpose === "password-reset";
  const recoveryVerified = mode === "reset-password" && location.state?.recoveryVerified === true;
  const recoveryProof = mode === "reset-password" ? location.state?.recoveryProof || "" : "";
  const recoveryEmail = mode === "reset-password" ? location.state?.email || "" : "";
  const isMfaSetup = mode === "mfa" && searchParams.get("setup") === "1";
  const pageSubtitle = isPasswordResetVerification
    ? "Confirm the code sent to your email before choosing a new password."
    : MODE_SUBTITLE[mode] || MODE_SUBTITLE.login;
  const modeHelp = isPasswordResetVerification
    ? "Enter the six-digit recovery code sent to your account email."
    : MODE_HELP[mode];
  const authLayout = useMemo(() => {
    const width = viewport.width;
    const height = viewport.height;
    const widthScale = (domain, range) =>
      d3.scaleLinear().domain(domain).range(range).clamp(true)(width);
    const heightScale = (domain, range) =>
      d3.scaleLinear().domain(domain).range(range).clamp(true)(height);
    const pagePaddingY = Math.round(heightScale([568, 1080], [10, 28]));
    const shellMaxWidth = Math.round(widthScale([980, 1920], [940, 1720]));
    const visualEdgeInsetX = Math.round(widthScale([980, 1920], [12, 18]));
    const visualEdgeInsetY = Math.round(heightScale([568, 1080], [10, 18]));
    return {
      shellMaxWidth,
      shellInset: Math.round(widthScale([320, 1920], [14, 30])),
      shellGap: Math.round(widthScale([980, 1920], [24, 48])),
      shellHeight: Math.max(0, height - pagePaddingY * 2),
      formWidth: Math.round(widthScale([320, 1920], [330, 520])),
      logoSize: Math.round(widthScale([320, 1920], [54, 72])),
      headingSize: Math.round(widthScale([320, 1920], [34, 46])),
      imageRadius: Math.round(widthScale([980, 1920], [18, 28])),
      visualBleedRight: Math.max(0, Math.round((width - shellMaxWidth) / 2 - visualEdgeInsetX)),
      visualBleedY: Math.max(0, pagePaddingY - visualEdgeInsetY),
      pagePaddingY,
      leftPaddingY: Math.round(heightScale([568, 1080], [8, 24])),
      formGap: heightScale([568, 900], [10, 16]),
      headerGap: heightScale([568, 900], [18, 28]),
      footerGap: heightScale([568, 900], [12, 20]),
    };
  }, [viewport.height, viewport.width]);
  const splitLayout = viewport.width >= 980;
  const stagedRegister = mode === "register" && !splitLayout && viewport.height < 720;
  const update = (name) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
    setError("");
    setRecoveryWarning("");
  };
  const completeFlow = (result, fallbackRole = "", postAuthTarget = "") => {
    if (result?.next === "mfa") {
      navigate("/auth/mfa", {
        replace: true,
        state: {
          challengeId: result.challenge_id || result.challengeId || "",
          postAuthTarget,
        },
      });
      return;
    }
    if (result?.next === "mfa-setup") {
      navigate("/auth/mfa?setup=1", { replace: true, state: { postAuthTarget } });
      return;
    }
    const nextUser = result?.user || result?.session?.user || user;
    if (nextUser) {
      navigate(postAuthTarget || getRoleHome(nextUser.role || fallbackRole), { replace: true });
    }
  };
  useEffect(() => {
    if (rawMode !== mode) {
      navigate("/auth/login", { replace: true });
    }
  }, [mode, navigate, rawMode]);
  useEffect(() => {
    setRegisterStage(0);
  }, [mode]);
  useEffect(() => {
    if (!requestedRegisterRole) {
      return;
    }
    setForm((current) =>
      current.role === requestedRegisterRole ? current : { ...current, role: requestedRegisterRole }
    );
  }, [requestedRegisterRole]);
  useEffect(() => {
    if (mode === "verify" && !["registration", "password-reset"].includes(verifyPurpose)) {
      navigate("/auth/login", { replace: true });
      return;
    }
    if (mode === "reset-password" && !recoveryVerified) {
      navigate("/auth/forgot-password", { replace: true });
    }
  }, [mode, navigate, recoveryVerified, verifyPurpose]);
  useEffect(() => {
    if (mode === "login" && status === "authenticated" && user) {
      navigate(getRoleHome(user.role), { replace: true });
    }
  }, [mode, navigate, status, user]);
  useEffect(() => {
    if (mode !== "callback") {
      return;
    }
    let active = true;
    setBusy(true);
    refreshSession()
      .then((payload) => {
        if (!active) {
          return;
        }
        const nextUser = payload?.user || payload?.session?.user;
        if (nextUser) {
          navigate(getRoleHome(nextUser.role), { replace: true });
        } else {
          setError("The authentication callback did not create an active session.");
        }
      })
      .finally(() => {
        if (active) {
          setBusy(false);
        }
      });
    return () => {
      active = false;
    };
  }, [mode, navigate, refreshSession]);
  useEffect(() => {
    if (!isMfaSetup) {
      return;
    }
    let active = true;
    setBusy(true);
    setupMfa()
      .then((payload) => {
        if (active) {
          setMfaSetup(payload);
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message || "Unable to start MFA setup.");
        }
      })
      .finally(() => {
        if (active) {
          setBusy(false);
        }
      });
    return () => {
      active = false;
    };
  }, [isMfaSetup, setupMfa]);
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    setRecoveryWarning("");
    try {
      if (mode === "login") {
        completeFlow(await login({ email: form.email.trim(), password: form.password }));
      } else if (mode === "register") {
        if (!form.accepted) {
          throw new Error("You must accept the Terms of Service and Privacy Policy.");
        }
        await register({
          email: form.email.trim(),
          password: form.password,
          role: form.role,
          accepted_terms: true,
          accepted_privacy: true,
          terms_version: LEGAL_VERSION,
          privacy_version: LEGAL_VERSION,
        });
        navigate("/auth/verify?purpose=registration", {
          replace: true,
          state: { email: form.email.trim(), registrationRole: form.role },
        });
      } else if (mode === "verify") {
        if (form.code.length !== 6) {
          throw new Error("Enter the complete six-digit verification code.");
        }
        if (verifyPurpose === "password-reset") {
          const result = await verifyPasswordReset({ email: form.email.trim(), code: form.code });
          const proof =
            result?.reset_token ||
            result?.resetToken ||
            result?.recovery_token ||
            result?.recoveryToken ||
            "";
          navigate("/auth/reset-password", {
            replace: true,
            state: {
              email: form.email.trim(),
              recoveryVerified: true,
              recoveryProof: proof,
            },
          });
        } else {
          const registrationRole = String(location.state?.registrationRole || "").toLowerCase();
          const verificationResult = await verifyEmail({ email: form.email.trim(), code: form.code });
          const verifiedRole = String(verificationResult?.user?.role || verificationResult?.session?.user?.role || registrationRole).toLowerCase();
          completeFlow(verificationResult, registrationRole, verifiedRole === "buyer" ? "/buyer/information" : "");
        }
      } else if (mode === "forgot-password") {
        const result = await forgotPassword(form.email.trim());
        const emailExists = result?.exists ?? result?.email_exists ?? result?.emailExists;
        if (emailExists === false) {
          setRecoveryWarning("No Aragon account exists for that email address.");
          return;
        }
        navigate("/auth/verify?purpose=password-reset", {
          replace: true,
          state: { email: form.email.trim() },
        });
      } else if (mode === "reset-password") {
        if (form.password !== form.confirmPassword) {
          throw new Error("The password confirmation does not match.");
        }
        const resetDetails = { email: recoveryEmail, password: form.password };
        if (recoveryProof) {
          resetDetails.token = recoveryProof;
        }
        await resetPassword(resetDetails);
        setMessage("Password updated. You can now return to sign in.");
      } else if (mode === "mfa") {
        if (form.code.length !== 6) {
          throw new Error("Enter the complete six-digit security code.");
        }
        completeFlow(
          await verifyMfa({
            code: form.code,
            challenge_id: location.state?.challengeId || "",
            setup: isMfaSetup,
          }),
          "",
          location.state?.postAuthTarget || ""
        );
      }
    } catch (requestError) {
      if (mode === "forgot-password" && requestError?.status === 404) {
        setRecoveryWarning("No Aragon account exists for that email address.");
      } else {
        setError(requestError.message || "The request could not be completed.");
      }
    } finally {
      setBusy(false);
    }
  };
  const resend = async () => {
    if (!form.email.trim()) {
      setError("Enter your email before requesting another verification code.");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    setRecoveryWarning("");
    try {
      if (isPasswordResetVerification) {
        await forgotPassword(form.email.trim());
      } else {
        await resendVerification(form.email.trim());
      }
      setMessage("A new verification code has been requested.");
    } catch (requestError) {
      setError(requestError.message || "Unable to resend the verification code.");
    } finally {
      setBusy(false);
    }
  };
  const renderForm = () => {
    if (mode === "callback") {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress size={30} sx={{ color: ARAGON_COLORS.navy }} />
          <Typography sx={{ color: ARAGON_COLORS.steel, fontSize: 12.5 }}>
            Validating your secure session…
          </Typography>
        </Stack>
      );
    }
    if (stagedRegister && registerStage === 0) {
      return (
        <Stack spacing={authLayout.formGap / 8}>
          <RoleChoice
            value={form.role}
            onChange={(role) => {
              setForm((current) => ({ ...current, role }));
              setError("");
            }}
          />
          <Button
            type="button"
            variant="contained"
            onClick={() => setRegisterStage(1)}
            sx={{
              minHeight: 44,
              borderRadius: "9px",
              bgcolor: ARAGON_COLORS.burgundy,
              color: ARAGON_COLORS.ivory,
              textTransform: "none",
              fontSize: 13.5,
              fontWeight: 750,
              "&:hover": { bgcolor: ARAGON_COLORS.wine },
            }}
          >
            Continue
          </Button>
        </Stack>
      );
    }
    return (
      <Box component="form" onSubmit={submit}>
        <Stack spacing={authLayout.formGap / 8}>
          {mode === "register" && !stagedRegister && (
            <RoleChoice
              value={form.role}
              onChange={(role) => {
                setForm((current) => ({ ...current, role }));
                setError("");
              }}
            />
          )}
          {["login", "register", "verify", "forgot-password"].includes(mode) && (
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          )}
          {mode === "login" && (
            <PasswordField
              label="Password"
              value={form.password}
              onChange={update("password")}
              action={
                <MuiLink
                  component={RouterLink}
                  to="/auth/forgot-password"
                  underline="none"
                  sx={{ color: ARAGON_COLORS.burgundy, fontSize: 11.5, ml: 2 }}
                >
                  Forgot password?
                </MuiLink>
              }
            />
          )}
          {mode === "register" && (
            <PasswordField
              label="Password"
              value={form.password}
              onChange={update("password")}
              autoComplete="new-password"
            />
          )}
          {mode === "register" && (
            <Typography
              sx={{
                mt: "4px !important",
                px: 0.2,
                fontSize: 10.8,
                lineHeight: 1.45,
                color: ARAGON_COLORS.steel,
              }}
            >
              Use at least 12 characters with English letters, numbers, and symbols.
            </Typography>
          )}
          {mode === "reset-password" && (
            <>
              <PasswordField
                label="New password"
                value={form.password}
                onChange={update("password")}
                autoComplete="new-password"
              />
              <PasswordField
                label="Confirm password"
                value={form.confirmPassword}
                onChange={update("confirmPassword")}
                autoComplete="new-password"
              />
            </>
          )}
          {["verify", "mfa"].includes(mode) && (
            <Box>
              <Typography sx={{ mb: 0.8, fontSize: 12.5, fontWeight: 650, color: ARAGON_COLORS.navy }}>
                Verification code
              </Typography>
              <OtpInput
                value={form.code}
                disabled={busy}
                onChange={(code) => {
                  setForm((current) => ({ ...current, code }));
                  setError("");
                }}
              />
            </Box>
          )}
          {isMfaSetup && mfaSetup?.secret && (
            <MessageBox>
              Authenticator setup key: <strong>{mfaSetup.secret}</strong>
            </MessageBox>
          )}
          {mode === "register" && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.accepted}
                  onChange={update("accepted")}
                  sx={{
                    color: ARAGON_COLORS.steel,
                    "&.Mui-checked": { color: ARAGON_COLORS.burgundy },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 11.5, color: ARAGON_COLORS.navy }}>
                  I agree to the{" "}
                  <MuiLink component={RouterLink} to="/terms" sx={{ color: ARAGON_COLORS.burgundy }}>
                    Terms of Service
                  </MuiLink>{" "}
                  and{" "}
                  <MuiLink component={RouterLink} to="/privacy" sx={{ color: ARAGON_COLORS.burgundy }}>
                    Privacy Policy
                  </MuiLink>
                  .
                </Typography>
              }
            />
          )}
          {modeHelp && (
            <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: ARAGON_COLORS.steel }}>
              {modeHelp}
            </Typography>
          )}
          {recoveryWarning && <MessageBox tone="warning">{recoveryWarning}</MessageBox>}
          {error && <MessageBox tone="error">{error}</MessageBox>}
          {message && <MessageBox>{message}</MessageBox>}
          <Button
            type="submit"
            variant="contained"
            disabled={busy}
            sx={{
              minHeight: 46,
              borderRadius: "9px",
              bgcolor: mode === "register" ? ARAGON_COLORS.burgundy : ARAGON_COLORS.navy,
              color: ARAGON_COLORS.ivory,
              textTransform: "none",
              fontSize: 13.5,
              fontWeight: 750,
              "&:hover": { bgcolor: ARAGON_COLORS.wine },
            }}
          >
            {busy
              ? "Working…"
              : mode === "login"
              ? "Sign in"
              : mode === "register"
              ? "Create account"
              : mode === "verify"
              ? isPasswordResetVerification
                ? "Verify code"
                : "Verify email"
              : mode === "forgot-password"
              ? "Send verification code"
              : mode === "reset-password"
              ? "Reset password"
              : "Verify"}
          </Button>
          {stagedRegister && mode === "register" && (
            <Button
              type="button"
              onClick={() => setRegisterStage(0)}
              sx={{
                minHeight: 30,
                p: 0,
                color: ARAGON_COLORS.steel,
                textTransform: "none",
                fontSize: 11.5,
                fontWeight: 650,
              }}
            >
              ← Change account type
            </Button>
          )}
        </Stack>
      </Box>
    );
  };
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        maxHeight: "100dvh",
        boxSizing: "border-box",
        bgcolor: AUTH_SURFACE,
        color: ARAGON_COLORS.navy,
        px: `${authLayout.shellInset}px`,
        py: `${authLayout.pagePaddingY}px`,
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      <MuiLink
        component={RouterLink}
        to="/"
        underline="none"
        sx={{
          position: "absolute",
          top: `${authLayout.shellInset}px`,
          left: `${authLayout.shellInset}px`,
          zIndex: 3,
          color: ARAGON_COLORS.steel,
          fontSize: { xs: 11.5, sm: 12.5 },
          fontWeight: 700,
          letterSpacing: "0.01em",
          "&:hover": {
            color: ARAGON_COLORS.burgundy,
          },
        }}
      >
        ← Back to Aragon
      </MuiLink>
      <Box
        sx={{
          width: "100%",
          height: `${authLayout.shellHeight}px`,
          maxHeight: `${authLayout.shellHeight}px`,
          maxWidth: `${authLayout.shellMaxWidth}px`,
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: splitLayout ? "minmax(0, 1.08fr) minmax(0, 0.92fr)" : "1fr",
          gap: splitLayout ? `${authLayout.shellGap}px` : 0,
          alignItems: "stretch",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            display: "grid",
            placeItems: "center",
            px: { xs: 0.5, sm: 2, md: 3 },
            py: `${authLayout.leftPaddingY}px`,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: `${authLayout.formWidth}px`, maxHeight: "100%", minHeight: 0 }}>
            <Box
              component="img"
              src={LOGO_SRC}
              alt="Aragon"
              sx={{
                width: `${authLayout.logoSize}px`,
                height: `${authLayout.logoSize}px`,
                objectFit: "contain",
                display: "block",
                mb: `${Math.round(authLayout.headerGap * 0.72)}px`,
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: `${authLayout.headingSize}px`,
                lineHeight: 1.03,
                fontWeight: 400,
                letterSpacing: "-0.035em",
                color: ARAGON_COLORS.navy,
              }}
            >
              {MODE_TITLE[mode] || MODE_TITLE.login}
            </Typography>
            <Typography
              sx={{
                mt: 1.4,
                maxWidth: 440,
                color: ARAGON_COLORS.steel,
                fontSize: { xs: 12, sm: 12.5 },
                lineHeight: 1.6,
              }}
            >
              {pageSubtitle}
            </Typography>
            <Box sx={{ mt: `${authLayout.headerGap}px` }}>{renderForm()}</Box>
            <Box sx={{ mt: `${authLayout.footerGap}px` }}>
              {mode === "login" && (
                <Typography sx={{ textAlign: "center", fontSize: 11.5, color: ARAGON_COLORS.steel }}>
                  New to Aragon?{" "}
                  <MuiLink component={RouterLink} to="/auth/register" sx={{ color: ARAGON_COLORS.burgundy }}>
                    Create an account
                  </MuiLink>
                </Typography>
              )}
              {mode === "register" && (
                <Typography sx={{ textAlign: "center", fontSize: 11.5, color: ARAGON_COLORS.steel }}>
                  Already registered?{" "}
                  <MuiLink component={RouterLink} to="/auth/login" sx={{ color: ARAGON_COLORS.burgundy }}>
                    Sign in
                  </MuiLink>
                </Typography>
              )}
              {mode === "verify" && (
                <Stack spacing={1.1} alignItems="center">
                  <Button
                    type="button"
                    onClick={resend}
                    disabled={busy}
                    sx={{
                      p: 0,
                      minWidth: 0,
                      color: ARAGON_COLORS.burgundy,
                      fontSize: 11.5,
                      fontWeight: 650,
                    }}
                  >
                    Send me a new code
                  </Button>
                  <MuiLink
                    component={RouterLink}
                    to={isPasswordResetVerification ? "/auth/forgot-password" : "/auth/login"}
                    sx={{ color: ARAGON_COLORS.steel, fontSize: 11.5 }}
                  >
                    {isPasswordResetVerification ? "Back to password recovery" : "Back to sign in"}
                  </MuiLink>
                </Stack>
              )}
              {mode === "forgot-password" && (
                <Box sx={{ textAlign: "center" }}>
                  <MuiLink component={RouterLink} to="/auth/login" sx={{ color: ARAGON_COLORS.burgundy, fontSize: 11.5 }}>
                    Back to sign in
                  </MuiLink>
                </Box>
              )}
              {mode === "reset-password" && (
                <Box sx={{ textAlign: "center" }}>
                  <MuiLink component={RouterLink} to="/auth/login" sx={{ color: ARAGON_COLORS.burgundy, fontSize: 11.5 }}>
                    Back to sign in
                  </MuiLink>
                </Box>
              )}
              {mode === "mfa" && (
                <Box sx={{ textAlign: "center" }}>
                  <MuiLink component={RouterLink} to="/auth/login" sx={{ color: ARAGON_COLORS.burgundy, fontSize: 11.5 }}>
                    Cancel and return to sign in
                  </MuiLink>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: splitLayout ? "block" : "none",
            minWidth: 0,
            minHeight: 0,
            width: `calc(100% + ${authLayout.visualBleedRight}px)`,
            height: `calc(100% + ${authLayout.visualBleedY * 2}px)`,
            mt: `-${authLayout.visualBleedY}px`,
            mb: `-${authLayout.visualBleedY}px`,
            mr: `-${authLayout.visualBleedRight}px`,
            alignSelf: "stretch",
            justifySelf: "start",
            borderRadius: `${authLayout.imageRadius}px`,
            overflow: "hidden",
            bgcolor: ARAGON_COLORS.navy,
            border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}`,
            boxShadow: `0 18px 52px ${alpha(ARAGON_COLORS.navy, 0.10)}`,
          }}
        >
          <Box
            component="img"
            src={AUTH_VISUAL_SRC}
            alt="Aragon"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "50% 50%",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Authenticate;
